"use client";

import { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  type MotionValue,
} from "framer-motion";
import {
  ArrowUpRight,
  Check,
  ChevronDown,
  ClipboardCheck,
  Contact,
  FileText,
  Home,
  LayoutGrid,
  MessageSquare,
  PencilLine,
  Plus,
  Search,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { jevCopy } from "@/lib/domain/jev/copy";

const SCENE_DURATION = 4700;
const CLOCK_UPDATE_INTERVAL = 32;
const TYPE_START = 400;
const TYPE_END = 1850;
const CLEAR_START = 4100;
const scenes = [
  { key: "JOBS", Icon: Search },
  { key: "TAILOR", Icon: PencilLine },
  { key: "ATS", Icon: TrendingUp },
  { key: "INTERVIEW", Icon: MessageSquare },
] as const;
const navigationItems = [
  { label: "SIDEBAR_HOME_SECTION_TITLE_TEXT", Icon: Home },
  { label: "SIDEBAR_ITEM_APP_TEXT", Icon: LayoutGrid },
  { label: "SIDEBAR_ITEM_CREATE_RESUME_TEXT", Icon: Plus },
  { label: "SIDEBAR_ITEM_FIND_JOB_TEXT", Icon: Search },
  { label: "SIDEBAR_ITEM_APPLY_JOB_TEXT", Icon: ClipboardCheck },
  { label: "SIDEBAR_ITEM_INTERVIEW_PREP_TEXT", Icon: Contact },
] as const;
type Phase = "typing" | "ready" | "clearing";

function PromptText({
  clock,
  prompts,
  reducedMotion,
}: {
  clock: MotionValue<number>;
  prompts: string[];
  reducedMotion: boolean;
}) {
  const textRef = useRef<HTMLSpanElement>(null);
  useMotionValueEvent(clock, "change", (elapsed) => {
    if (!textRef.current) return;
    if (reducedMotion) {
      textRef.current.textContent = prompts[0];
      return;
    }
    const prompt = prompts[Math.floor(elapsed / SCENE_DURATION) % prompts.length];
    const time = elapsed % SCENE_DURATION;
    const fraction = time >= CLEAR_START
      ? 1 - (time - CLEAR_START) / (SCENE_DURATION - CLEAR_START)
      : (time - TYPE_START) / (TYPE_END - TYPE_START);
    const count = Math.round(prompt.length * Math.max(0, Math.min(1, fraction)));
    const next = prompt.slice(0, count);
    if (textRef.current.textContent !== next) textRef.current.textContent = next;
  });
  return <span ref={textRef} data-agent-prompt>{reducedMotion ? prompts[0] : ""}</span>;
}

export default function AgentHeroAnimation() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith("es") ? "es" : "en";
  const copy = jevCopy[locale];
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { amount: 0.15 });
  const reducedMotion = Boolean(useReducedMotion());
  const [pageVisible, setPageVisible] = useState(true);
  const [scene, setScene] = useState({ index: 0, phase: "typing" as Phase });
  const sceneRef = useRef(scene);
  const clock = useMotionValue(0);
  const prompts = scenes.map(({ key }) => t(`HOME_AGENT_PROMPT_${key}`));
  const phase = reducedMotion ? "ready" : scene.phase;
  const current = scenes[reducedMotion ? 0 : scene.index];

  useMotionValueEvent(clock, "change", (elapsed) => {
    const index = Math.floor(elapsed / SCENE_DURATION) % scenes.length;
    const time = elapsed % SCENE_DURATION;
    const nextPhase: Phase = time >= CLEAR_START ? "clearing" : time >= TYPE_END ? "ready" : "typing";
    if (sceneRef.current.index !== index || sceneRef.current.phase !== nextPhase) {
      sceneRef.current = { index, phase: nextPhase };
      setScene(sceneRef.current);
    }
  });

  useEffect(() => {
    const update = () => setPageVisible(document.visibilityState === "visible");
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    clock.set(0);
  }, [clock, locale]);

  useEffect(() => {
    if (!inView || !pageVisible || reducedMotion) return;
    let frame: number;
    let previous: number | undefined;
    const tick = (time: number) => {
      if (previous === undefined) {
        previous = time;
      } else if (time - previous >= CLOCK_UPDATE_INTERVAL) {
        clock.set(clock.get() + Math.min(time - previous, 100));
        previous = time;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [clock, inView, pageVisible, reducedMotion]);

  return (
    <div ref={rootRef} data-agent-preview className="relative mx-auto w-full">
      <div className="pointer-events-none absolute -inset-6 rounded-[48px] bg-[radial-gradient(ellipse_at_75%_15%,rgba(129,140,248,0.22),transparent_60%),radial-gradient(ellipse_at_15%_85%,rgba(52,211,153,0.18),transparent_55%)] blur-2xl" aria-hidden="true" />
      <div className="relative rounded-[26px] border border-white/80 bg-white/50 p-1.5 shadow-[0_30px_90px_-35px_rgba(30,41,59,0.36)] backdrop-blur-xl dark:border-white/15 dark:bg-white/5">
        <div aria-hidden="true" className="relative overflow-hidden rounded-[21px] border border-slate-200/80 bg-slate-50 text-slate-900 dark:border-white/10 dark:bg-slate-950 dark:text-white">
          <div className="flex">
            <aside className="flex w-28 shrink-0 flex-col border-r border-slate-200/60 bg-white/60 px-2.5 py-4 dark:border-white/10 dark:bg-white/[0.02] sm:w-40 sm:px-3">
              <div className="mb-5 flex items-center gap-2 px-1 text-[11px] font-semibold sm:text-xs">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/logo.svg" alt="" className="h-5 w-5 dark:invert" />
                LandFast
              </div>
              <div className="mb-5 flex items-center gap-2 rounded-xl border border-slate-200/70 bg-white/80 p-2 text-[10px] font-semibold text-slate-600 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-300 sm:text-[11px]">
                <span className="rounded-lg bg-indigo-100 p-1.5 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300"><Sparkles className="h-3.5 w-3.5" /></span>
                {t("SIDEBAR_AGENT_TITLE_TEXT")}
              </div>
              <p className="mb-3 px-1 text-[7px] font-semibold uppercase tracking-[0.15em] text-slate-400 sm:text-[8px]">{t("SIDEBAR_NAVIGATION_TITLE_TEXT")}</p>
              <div className="space-y-4">
                {navigationItems.map(({ label, Icon }) => (
                  <div key={label} className="flex items-center gap-2 px-1 text-[9px] text-slate-500 dark:text-slate-400 sm:gap-2.5 sm:text-[11px]">
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span>{t(label)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-auto flex items-center gap-2 rounded-xl border border-slate-200/70 bg-white/70 px-2 py-2 dark:border-white/10 dark:bg-white/5">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500"><span className="h-2 w-2 rounded-full bg-white/90" /></div>
                <div className="min-w-0 text-[8px] leading-3 text-slate-500 dark:text-slate-400 sm:text-[9px]">
                  <span className="block uppercase tracking-wide">{t("SIDEBAR_ACCOUNT_TEXT")}</span>
                  <span className="block text-slate-600 dark:text-slate-300">{t("SIDEBAR_CONFIGURATION_TITLE_TEXT")}</span>
                </div>
              </div>
            </aside>
            <div className="relative min-w-0 flex-1 bg-[radial-gradient(ellipse_at_top_right,rgba(199,210,254,0.4),transparent_65%),radial-gradient(ellipse_at_bottom_left,rgba(167,243,208,0.25),transparent_60%)] px-3 pb-7 pt-7 dark:bg-[radial-gradient(ellipse_at_top_right,rgba(79,70,229,0.12),transparent_65%)] sm:px-6 sm:pb-9 sm:pt-9">
              <div className="mb-5 text-center sm:mb-6">
                <div className="mb-2 flex items-center justify-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-indigo-500 dark:text-indigo-300"><Sparkles className="h-3 w-3" />{t("HOME_AGENT_EYEBROW")}</div>
                <div className="text-lg font-bold leading-tight tracking-[-0.035em] sm:text-[25px]">{copy.title}</div>
                <p className="mx-auto mt-2 max-w-[310px] text-[10px] leading-4 text-slate-500 dark:text-slate-400 sm:text-[11px]">{copy.subtitle}</p>
              </div>
              <div className="relative rounded-[20px] border border-white/90 bg-white/90 p-3.5 shadow-[0_20px_50px_-24px_rgba(15,23,42,0.3)] dark:border-white/10 dark:bg-slate-900/85 sm:p-5">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-[9px] text-slate-500 dark:text-slate-400 sm:text-[10px]">
                  <span className="flex min-w-0 items-center gap-1.5"><FileText className="h-3 w-3 shrink-0" /><span className="truncate">my-resume.pdf</span><span className="flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15"><Check className="h-2 w-2" /></span></span>
                  <span className="flex shrink-0 items-center gap-1 rounded-full border border-slate-200 px-2 py-1.5 dark:border-white/15">{t("HOME_AGENT_ROLE")}<ChevronDown className="h-2.5 w-2.5" /></span>
                </div>
                <motion.div
                  animate={{ borderColor: phase === "typing" ? "rgba(129,140,248,0.7)" : "rgba(203,213,225,0.6)" }}
                  transition={{ duration: 0.4 }}
                  className="relative flex min-h-[132px] flex-col rounded-xl border bg-slate-50/20 px-3 py-3 text-[13px] font-medium leading-[1.65] text-slate-700 dark:bg-slate-950/30 dark:text-slate-200 sm:min-h-[120px] sm:px-4 sm:text-[16px]"
                >
                  <div className="min-h-0 flex-1">
                    <PromptText clock={clock} prompts={prompts} reducedMotion={reducedMotion} />
                  </div>
                  <div className="mt-2 flex items-end gap-1.5">
                    <div className="flex min-w-0 flex-1 flex-wrap gap-1 pr-1">
                      {scenes.map(({ key, Icon }, index) => (
                        <motion.div
                          key={key}
                          animate={{
                            backgroundColor: current.key === key ? "rgba(99,102,241,0.11)" : "rgba(148,163,184,0.1)",
                            borderColor: current.key === key ? "rgba(129,140,248,0.5)" : "rgba(148,163,184,0.28)",
                          }}
                          className="inline-flex min-h-6 shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[8px] font-medium text-slate-600 shadow-sm transition-colors dark:text-slate-300 sm:min-h-7 sm:px-2.5 sm:text-[9px]"
                        >
                          <Icon className={`h-3 w-3 shrink-0 ${scene.index === index ? "text-indigo-500 dark:text-indigo-300" : "text-slate-400"}`} />
                          {t(`HOME_AGENT_ACTION_${key}`)}
                        </motion.div>
                      ))}
                    </div>
                    <span className="mb-0.5 shrink-0 text-[8px] text-slate-400">↵</span>
                  </div>
                </motion.div>
                <div className="mt-4 flex h-9 items-center justify-between gap-2 sm:mt-5">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={`${current.key}-${phase}`}
                      initial={reducedMotion ? false : { opacity: 0, y: 5 }}
                      animate={{ opacity: phase === "clearing" ? 0 : 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.2 }}
                      className="flex min-w-0 items-center gap-1.5 text-[9px] text-slate-500 dark:text-slate-400 sm:text-[10px]"
                    >
                      {phase === "ready" ? <Check className="h-3 w-3 shrink-0 text-emerald-500" /> : <Sparkles className="h-3 w-3 shrink-0 text-indigo-400" />}
                      <span className="truncate">{phase === "ready" ? t(`HOME_AGENT_ACTION_${current.key}`) : t("HOME_AGENT_ROUTING")}</span>
                    </motion.span>
                  </AnimatePresence>
                  <motion.div
                    animate={{ opacity: phase === "ready" ? 1 : 0.5, scale: phase === "ready" ? [1, 0.97, 1] : 1 }}
                    transition={{ duration: 0.4 }}
                    className="flex shrink-0 items-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400 px-3 py-2 text-[10px] font-semibold text-white shadow-md shadow-indigo-500/20"
                  >
                    {copy.continue}<span className="rounded-full bg-white/20 p-1"><ArrowUpRight className="h-2.5 w-2.5" /></span>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <span className="sr-only">{t("HOME_AGENT_DESCRIPTION")}</span>
    </div>
  );
}
